import * as React from "react";
import { Button, CircularProgress } from "@material-ui/core";
import { useSnackbar } from "notistack";
import TextField from "@material-ui/core/TextField";
import FormHelperText from "@material-ui/core/FormHelperText";
import Typography from "@material-ui/core/Typography";
import AssignmentIndIcon from "@material-ui/icons/AssignmentInd";
import Autocomplete from "@material-ui/lab/Autocomplete";
import { apiPost, apiGet } from "../services/api";
import ModalDialog, { useModalDialog } from "./ModalDialog";
import IUser from "../models/IUser";

export interface IUsersAutoCompleteProps {
    onUserChanged(event: object, value: IUser | undefined): void;
    defaultValue?: IUser | undefined;
}

export function UsersAutoComplete(props: IUsersAutoCompleteProps) {
    const [open, setOpen] = React.useState(false);
    const [options, setOptions] = React.useState<IUser[]>([]);
    const [loading, setLoading] = React.useState(false);

    React.useEffect(() => {
        if (!open) {
            return;
        }
        const getOptions = async () => {
            setLoading(true);
            const url: string = `users/all`;
            try {
                const response = await apiGet(url);
                setOptions(response as IUser[]);
            } catch {
                setOptions([]);
            } finally {
                setLoading(false);
            }
        };
        getOptions();
        return () => {
            setOptions([]);
        };
    }, [open]);

    const getOptionLabel = (option: IUser) => {
        const parts = [option.firstName, option.lastName];
        const name = parts.filter(x => x?.length).join(" ");
        return name.length ? `${name} [${option.email}]` : `[${option.email}]`;
    };

    return (
        <Autocomplete
            open={open}
            onChange={(event: any, values: any) => {
                props.onUserChanged(event, values);
            }}
            onOpen={() => {
                setOpen(true);
            }}
            onClose={() => {
                setOpen(false);
            }}
            defaultValue={props.defaultValue}
            getOptionSelected={(option, value) => option.id === value.id}
            getOptionLabel={getOptionLabel}
            options={options}
            loading={loading}
            renderInput={params => (
                <TextField
                    {...params}
                    label="Search existing customers"
                    fullWidth
                    variant="outlined"
                    InputProps={{
                        ...params.InputProps,
                        endAdornment: (
                            <React.Fragment>
                                {loading ? (
                                    <CircularProgress
                                        color="inherit"
                                        size={20}
                                    />
                                ) : null}
                                {params.InputProps.endAdornment}
                            </React.Fragment>
                        )
                    }}
                />
            )}
        />
    );
}

const Loading: React.FC = () => {
    return (
        <div
            style={{
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                height: "100%"
            }}
        >
            <div style={{ textAlign: "center" }}>
                <CircularProgress />
            </div>
        </div>
    );
};

interface ICustomer {
    id?: string;
    email?: string;
    givenName?: string;
    middleName?: string;
    familyName?: string;
    phoneNumber?: string;
}

interface INewCustomerState {
    onCustomerChanged: (customer: ICustomer) => void;
}

interface IAddCustomerState {
    confirmed: boolean;
    onCustomerChanged: (customer: ICustomer) => void;
    siteName: string;
    userDisplayName: string;
}

interface IAddCustomer {
    onSuccess: (user: IUser) => void;
    projectId: number;
    projectName: string;
}

const NewCustomerState: React.FC<INewCustomerState> = ({
    onCustomerChanged
}) => {
    const [email, setEmail] = React.useState("");
    const [givenName, setGivenName] = React.useState("");
    const [familyName, setFamilyName] = React.useState("");
    const [phoneNumber, setPhoneNumber] = React.useState("");

    const onEmailChanged = (event: {
        target: { value: React.SetStateAction<string> };
    }) => {
        setEmail(event.target.value);
    };

    const onGivenNameChanged = (event: {
        target: { value: React.SetStateAction<string> };
    }) => {
        setGivenName(event.target.value);
    };

    const onFamilyNameChanged = (event: {
        target: { value: React.SetStateAction<string> };
    }) => {
        setFamilyName(event.target.value);
    };

    const onPhoneNumberChanged = (event: {
        target: { value: React.SetStateAction<string> };
    }) => {
        setPhoneNumber(event.target.value);
    };

    React.useEffect(() => {
        const customer = {
            email,
            givenName,
            familyName,
            phoneNumber
        };
        onCustomerChanged(customer);
    }, [email, givenName, familyName, phoneNumber]);

    return (
        <React.Fragment>
            <TextField
                label="E-Mail Address"
                onChange={onEmailChanged}
                type="email"
                variant="outlined"
                required
                fullWidth
                inputProps={{ maxLength: 450 }}
            />
            <br />
            <br />
            <TextField
                label="First Name"
                onChange={onGivenNameChanged}
                variant="outlined"
                required
                fullWidth
                inputProps={{ maxLength: 1024 }}
            />
            <br />
            <br />
            <TextField
                label="Last Name"
                onChange={onFamilyNameChanged}
                variant="outlined"
                required
                fullWidth
                inputProps={{ maxLength: 1024 }}
            />
            <br />
            <br />
            <TextField
                label="Phone Number"
                onChange={onPhoneNumberChanged}
                variant="outlined"
                fullWidth
                inputProps={{ maxLength: 1024 }}
            />
        </React.Fragment>
    );
};

const AddCustomerState: React.FC<IAddCustomerState> = ({
    siteName,
    confirmed,
    onCustomerChanged,
    userDisplayName
}) => {
    const [newCustomer, setNewCustomer] = React.useState(false);

    const handleCustomerChanged = (user: IUser | undefined) => {
        onCustomerChanged({ id: user?.id, email: user?.email });
    };

    const handleNewCustomer = () => {
        setNewCustomer(true);
    };

    if (confirmed) {
        return (
            <React.Fragment>
                <div style={{ minHeight: "80px" }}>
                    <Typography variant="body1">
                        {userDisplayName} will recieve an email invitation to
                        access the site {siteName}. Do you want to continue?
                    </Typography>
                </div>
            </React.Fragment>
        );
    }

    if (newCustomer) {
        return (
            <React.Fragment>
                <NewCustomerState onCustomerChanged={onCustomerChanged} />
            </React.Fragment>
        );
    }

    return (
        <React.Fragment>
            <UsersAutoComplete
                onUserChanged={(_, user) => handleCustomerChanged(user)}
            />
            <FormHelperText>
                Select an existing customer or create a new customer below
            </FormHelperText>
            <br />
            <Button
                variant="outlined"
                color="primary"
                style={{ fontWeight: 400 }}
                onClick={handleNewCustomer}
            >
                User not listed?
            </Button>
        </React.Fragment>
    );
};

const AddCustomer: React.FC<IAddCustomer> = ({
    projectName: siteName,
    onSuccess,
    projectId: siteId
}) => {
    const defaultConfirmButtonText = "Add Customer";

    const [modalOpen, , toggleModal] = useModalDialog();
    const [loading, setLoading] = React.useState(false);
    const [errorText, setErrorText] = React.useState<string | undefined>(
        undefined
    );
    const [customer, setCustomer] = React.useState<ICustomer | undefined>(
        undefined
    );
    const [customerValid, setCustomerValid] = React.useState(false);
    const [confirmButtonText, setConfirmButtonText] = React.useState(
        defaultConfirmButtonText
    );
    const [confirmed, setConfirmed] = React.useState(false);

    const snackbar = useSnackbar();

    const handleOpen = () => {
        toggleModal();
    };

    const handleClose = () => {
        toggleModal();
    };

    React.useEffect(() => {
        setErrorText(undefined);
        setConfirmed(false);
        setConfirmButtonText(defaultConfirmButtonText);
    }, [modalOpen]);

    React.useEffect(() => {
        if (customer) {
            setErrorText(undefined);
        }
    }, [customer]);

    React.useEffect(() => {
        const addCustomer = !!customer && !!customer.id;
        const newCustomer =
            !!customer &&
            !!customer?.email &&
            !!customer.givenName &&
            !!customer.familyName;

        setCustomerValid(addCustomer || newCustomer);
    }, [customer]);

    const handleConfirm = async () => {
        if (!confirmed) {
            setConfirmButtonText("Send Invitation");
            setConfirmed(true);
            return;
        }

        const payload = {
            projectIds: [siteId],
            userId: customer?.id,
            email: customer?.email,
            firstName: customer?.givenName,
            lastName: customer?.familyName,
            phoneNumber: customer?.phoneNumber
        };

        const promise = apiPost(`users/invited`, payload);

        setLoading(true);

        promise
            .then(user => {
                snackbar.enqueueSnackbar(
                    `Successfully added customer to site`,
                    { variant: "success" }
                );
                handleClose();
                onSuccess(user);
            })
            .catch(err => {
                setErrorText(err.body?.errorMessage);
                setConfirmed(false);
                setConfirmButtonText(defaultConfirmButtonText);
                setCustomer(undefined);
            })
            .finally(() => {
                setLoading(false);
            });
    };

    const onCustomerChanged = (customer: ICustomer | undefined) => {
        setCustomer(customer);
    };

    return (
        <React.Fragment>
            <Button
                variant="outlined"
                color="primary"
                onClick={handleOpen}
                startIcon={<AssignmentIndIcon />}
            >
                Add Customer
            </Button>
            <ModalDialog
                isActive={modalOpen}
                title={`Add Customer to ${siteName}`}
                confirmDisabled={!customerValid}
                confirmAction={handleConfirm}
                confirmButtonText={confirmButtonText}
                handleClose={handleClose}
                preventClose={loading}
                showError={!!errorText}
                errorText={errorText}
            >
                {loading ? (
                    <div style={{ height: "100px" }}>
                        <Loading />
                    </div>
                ) : (
                    <React.Fragment>
                        <AddCustomerState
                            userDisplayName={`${customer?.email}`}
                            onCustomerChanged={onCustomerChanged}
                            confirmed={confirmed}
                            siteName={siteName}
                        />
                    </React.Fragment>
                )}
            </ModalDialog>
        </React.Fragment>
    );
};

export default AddCustomer;
